/// <reference types='vite/client' />
import { type ClassValue, clsx } from 'clsx';
import { format, formatDistanceToNow, parseISO } from 'date-fns';

// Class name utility (for combining Tailwind classes)
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

// Price formatting
export function formatPrice(price: number | undefined, currency: string = 'USD'): string {
  if (price === undefined || price === null) return 'N/A';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
}

// Date formatting
export function formatDate(date: string | Date, formatString: string = 'MMM d, yyyy'): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, formatString);
}

export function formatRelativeTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return formatDistanceToNow(dateObj, { addSuffix: true });
}

export function timeUntil(date: string | Date): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const now = new Date();
  if (dateObj <= now) return 'Ended';
  return formatDistanceToNow(dateObj, { addSuffix: false });
}

// Number formatting
export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

export function formatCompactNumber(num: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(num);
}

// Rating helpers
export function formatRating(rating: number): string {
  return rating.toFixed(1);
}

export function getRatingColor(rating: number): string {
  if (rating >= 4.5) return 'text-green-600';
  if (rating >= 4.0) return 'text-green-500';
  if (rating >= 3.5) return 'text-yellow-500';
  if (rating >= 3.0) return 'text-orange-500';
  return 'text-red-500';
}

// Card condition helpers
export function getConditionColor(condition: string): string {
  const colors: Record<string, string> = {
    'Mint': 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200',
    'Near Mint': 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200',
    'Excellent': 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200',
    'Good': 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200',
    'Played': 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200',
    'Poor': 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200',
  };
  return colors[condition] || 'bg-gray-100 dark:bg-zinc-800 text-gray-800 dark:text-zinc-200';
}

// Status helpers
export function getOrderStatusColor(status: string): string {
  const colors: Record<string, string> = {
    'pending': 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200',
    'paid': 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200',
    'processing': 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200',
    'shipped': 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-200',
    'delivered': 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200',
    'completed': 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200',
    'cancelled': 'bg-gray-100 dark:bg-zinc-800 text-gray-800 dark:text-zinc-200',
    'refunded': 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200',
    'disputed': 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200',
  };
  return colors[status] || 'bg-gray-100 dark:bg-zinc-800 text-gray-800 dark:text-zinc-200';
}

export function getListingStatusColor(status: string): string {
  const colors: Record<string, string> = {
    'active': 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200',
    'sold': 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200',
    'cancelled': 'bg-gray-100 dark:bg-zinc-800 text-gray-800 dark:text-zinc-200',
    'expired': 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200',
  };
  return colors[status] || 'bg-gray-100 dark:bg-zinc-800 text-gray-800 dark:text-zinc-200';
}

// URL helpers
export function buildImageUrl(url: string | undefined): string {
  if (!url) return '/placeholder-card.png';
  if (url.startsWith('http')) return url;
  return `${import.meta.env.VITE_API_URL}${url}`;
}

// Validation helpers
export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function isValidUsername(username: string): boolean {
  return /^[A-Za-z0-9_]{3,50}$/.test(username);
}

// Search helpers
export function highlightSearchTerm(text: string, searchTerm: string): string {
  if (!searchTerm) return text;
  
  const regex = new RegExp(`(${searchTerm})`, 'gi');
  return text.replace(regex, '<mark class="bg-yellow-200">$1</mark>');
}

// Debounce function
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

// Truncate text
export function truncate(text: string, length: number = 100): string {
  if (text.length <= length) return text;
  return text.slice(0, length) + '...';
}

// Copy to clipboard
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
}

// Download file
export function downloadFile(url: string, filename: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Price change helpers
export function calculatePriceChange(oldPrice: number, newPrice: number): {
  amount: number;
  percentage: number;
  isIncrease: boolean;
} {
  const amount = newPrice - oldPrice;
  const percentage = (amount / oldPrice) * 100;
  return {
    amount,
    percentage,
    isIncrease: amount > 0,
  };
}

export function getPriceChangeColor(isIncrease: boolean): string {
  return isIncrease ? 'text-red-600' : 'text-green-600';
}

// SEO helpers
export function generatePageTitle(title: string): string {
  return `${title} | Open Card Vault`;
}

export function generateMetaDescription(description: string): string {
  return truncate(description, 160);
}